import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Conversation, Me, Message, ReplyRef } from '../api/types';

/**
 * Local cache (offline display) and outbox (unsent messages survive reloads).
 * When E2EE arrives, decrypted history and device keys will live here as well.
 */
export interface OutboxItem {
  clientId: string;
  conversationId: string;
  kind: 'text' | 'media';
  text?: string;
  file?: Blob;
  fileName?: string;
  attachmentId?: string; // set once the upload succeeded, so retries do not re-upload
  voiceDurationMs?: number; // set for voice recordings
  replyTo?: ReplyRef; // quoted message (its id is sent; the rest is shown while pending)
  forward?: Message; // a copy of this message is sent (POST /forward); shown as it is while pending
  createdAt: string;
  failed?: boolean;
  retryable?: boolean; // failed for a transient reason; retried automatically on reconnect
  error?: string;
}

/**
 * Persisted form: the file is stored as an ArrayBuffer. Safari (WebKit) fails to store File/Blob objects
 * from <input type=file> in IndexedDB, or returns them empty when read back.
 */
interface StoredOutboxItem extends Omit<OutboxItem, 'file'> {
  fileData?: ArrayBuffer;
  fileType?: string;
}

// Larger files are kept in memory only: they are sent in this session but not restored after a reload.
const PERSIST_FILE_MAX = 25 * 1024 * 1024;

interface EvoDB extends DBSchema {
  kv: { key: string; value: unknown };
  messages: { key: [string, number]; value: Message; indexes: { byConversation: string } };
  outbox: { key: string; value: StoredOutboxItem; indexes: { byCreated: string } };
}

const CACHE_PER_CONVERSATION = 200;

let dbp: Promise<IDBPDatabase<EvoDB>> | null = null;
function db() {
  dbp ??= openDB<EvoDB>('evo', 1, {
    upgrade(d) {
      d.createObjectStore('kv');
      d.createObjectStore('messages', { keyPath: ['conversationId', 'seq'] }).createIndex('byConversation', 'conversationId');
      d.createObjectStore('outbox', { keyPath: 'clientId' }).createIndex('byCreated', 'createdAt');
    },
  });
  return dbp;
}

// IndexedDB can be unavailable (private mode, storage pressure); the app must keep working without it.
async function safe<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch {
    return fallback;
  }
}

export const local = {
  getMe: () => safe(async () => (await (await db()).get('kv', 'me')) as Me | undefined, undefined),
  setMe: (me: Me) => safe(async () => void (await (await db()).put('kv', me, 'me')), undefined),

  getConversations: () => safe(async () => ((await (await db()).get('kv', 'conversations')) as Conversation[]) ?? [], []),
  setConversations: (list: Conversation[]) => safe(async () => void (await (await db()).put('kv', list, 'conversations')), undefined),

  getMessages: (conversationId: string) =>
    safe(async () => {
      const all = await (await db()).getAllFromIndex('messages', 'byConversation', conversationId);
      return all.sort((a, b) => a.seq - b.seq).slice(-CACHE_PER_CONVERSATION);
    }, [] as Message[]),

  putMessages: (messages: Message[]) =>
    safe(async () => {
      if (!messages.length) return;
      const d = await db();
      const t = d.transaction('messages', 'readwrite');
      await Promise.all([...messages.map((m) => t.store.put(m)), t.done]);
    }, undefined),

  /** Keeps only the newest N messages per conversation. */
  trimMessages: (conversationId: string) =>
    safe(async () => {
      const d = await db();
      const keys = await d.getAllKeysFromIndex('messages', 'byConversation', conversationId);
      if (keys.length <= CACHE_PER_CONVERSATION) return;
      keys.sort((a, b) => a[1] - b[1]);
      const t = d.transaction('messages', 'readwrite');
      await Promise.all([...keys.slice(0, keys.length - CACHE_PER_CONVERSATION).map((k) => t.store.delete(k)), t.done]);
    }, undefined),

  outbox: {
    all: () =>
      safe(async () => {
        const stored = await (await db()).getAllFromIndex('outbox', 'byCreated');
        return stored.map(({ fileData, fileType, ...rest }): OutboxItem => ({
          ...rest,
          file: fileData ? new Blob([fileData], { type: fileType }) : undefined,
        }));
      }, [] as OutboxItem[]),
    put: (item: OutboxItem) =>
      safe(async () => {
        const { file, ...rest } = item;
        const stored: StoredOutboxItem = rest;
        // Once uploaded, only the attachment id is needed to retry.
        if (file && !item.attachmentId && file.size <= PERSIST_FILE_MAX) {
          stored.fileData = await file.arrayBuffer();
          stored.fileType = file.type;
        }
        await (await db()).put('outbox', stored);
      }, undefined),
    delete: (clientId: string) => safe(async () => (await db()).delete('outbox', clientId), undefined),
  },

  clear: () =>
    safe(async () => {
      const d = await db();
      await Promise.all([d.clear('kv'), d.clear('messages'), d.clear('outbox')]);
    }, undefined),
};
